import { REGIONS, type RegionId } from '@blorbs/common';

const AUTO_CONFIRM_MS = 10_000;

interface RoomStatus {
  region: RegionId | null;
  suggested: RegionId | null;
}

/**
 * Where should this match live? The first player to arrive picks (or lets the
 * countdown pick the lowest ping); everyone after them joins that room. Pings
 * are measured through the real path — Discord proxy → Cloudflare → a probe
 * object pinned to each region — so the numbers are what the match will feel like.
 */
export async function resolveRegion(apiBase: string, room: string): Promise<RegionId | null> {
  // If the directory is unreachable (deploy in progress, single-region host), skip
  // the picker: the server still places the room by country.
  const status = await getJson<RoomStatus>(`${apiBase}/api/room?room=${encodeURIComponent(room)}`);
  if (!status) return null;
  if (status.region) return status.region;

  const picked = await pickRegion(apiBase, status.suggested);
  const res = await getJson<{ region: RegionId }>(`${apiBase}/api/room?room=${encodeURIComponent(room)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ region: picked }),
  });
  return res?.region ?? picked; // may differ from `picked` if a friend was faster
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const r = await fetch(url, init);
    if (!r.ok || !(r.headers.get('content-type') ?? '').includes('json')) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

export const regionName = (id: string): string => REGIONS.find((r) => r.id === id)?.name ?? id;

function pickRegion(apiBase: string, suggested: RegionId | null): Promise<RegionId> {
  const form = document.getElementById('name-form')!;
  const overlay = document.getElementById('overlay')!;
  overlay.classList.remove('hidden');

  const title = el('h1', 'Blorbs');
  const lead = el('p', 'Where should this match run?');
  const list = document.createElement('ul');
  list.className = 'regions';
  const button = document.createElement('button');
  button.type = 'button';
  const note = el('p', 'Friends who join this channel play in the room you pick.');
  note.className = 'note';
  form.replaceChildren(title, lead, list, button, note);

  interface Row {
    li: HTMLLIElement;
    ms: HTMLSpanElement;
    where: HTMLElement;
    ping: number;
  }
  const rows = new Map<RegionId, Row>();
  for (const r of REGIONS) {
    const li = document.createElement('li');
    const name = el('span', r.name);
    const where = el('small', r.hint);
    const ms = document.createElement('span');
    ms.className = 'ms';
    ms.textContent = '…';
    li.append(name, where, ms);
    list.append(li);
    rows.set(r.id, { li, ms, where, ping: Infinity });
  }

  let selected: RegionId | null = suggested;
  let userChose = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let deadline = 0;

  const render = (): void => {
    const sorted = [...rows.entries()].sort((a, b) => a[1].ping - b[1].ping);
    for (const [id, row] of sorted) {
      list.append(row.li);
      row.li.classList.toggle('selected', id === selected);
    }
    const secs = deadline ? Math.max(0, Math.ceil((deadline - performance.now()) / 1000)) : null;
    button.textContent = selected ? `Play in ${regionName(selected)}${secs !== null ? ` (${secs})` : ''}` : 'Measuring…';
    button.disabled = !selected;
  };

  return new Promise<RegionId>((resolve) => {
    const finish = (): void => {
      if (timer) clearInterval(timer);
      form.replaceChildren(el('h1', 'Blorbs'), el('p', `Joining a room in ${regionName(selected!)}…`));
      resolve(selected!);
    };

    for (const [id, row] of rows) {
      row.li.addEventListener('click', () => {
        selected = id;
        userChose = true;
        render();
      });
    }
    button.addEventListener('click', () => selected && finish());

    // Measure a few regions at a time (so they don't queue on each other);
    // the first result starts the countdown.
    let best = Infinity;
    const onResult = (id: RegionId, row: Row, ping: number, colo: string | null): void => {
      row.ping = ping;
      row.ms.textContent = ping === Infinity ? '—' : `${Math.round(ping)} ms`;
      if (colo) row.where.textContent = colo;
      if (ping < best) {
        best = ping;
        if (!userChose) selected = id; // follow the best measurement unless the player clicked
      }
      if (!deadline && ping !== Infinity) {
        deadline = performance.now() + AUTO_CONFIRM_MS;
        timer = setInterval(() => {
          render();
          if (performance.now() >= deadline && selected) finish();
        }, 250);
      }
      render();
    };
    const queue = [...rows.entries()];
    const worker = async (): Promise<void> => {
      for (let next = queue.shift(); next; next = queue.shift()) {
        const [id, row] = next;
        const r = await measure(apiBase, id);
        onResult(id, row, r.ping, r.colo);
      }
    };
    void Promise.all([worker(), worker(), worker()]);
    render();
  });
}

/**
 * Same transport as a match: open a WebSocket to the region's probe object,
 * then time three echoes on the open socket and keep the best. Connecting
 * (which includes any cold start) is excluded from the number.
 */
function measure(apiBase: string, region: RegionId): Promise<{ ping: number; colo: string | null }> {
  return new Promise((resolve) => {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${proto}//${location.host}${apiBase}/probe?region=${region}`);
    } catch {
      resolve({ ping: Infinity, colo: null });
      return;
    }
    let colo: string | null = null;
    let best = Infinity;
    let sent = 0;
    let t0 = 0;
    const done = (): void => {
      clearTimeout(guard);
      try {
        ws.close();
      } catch {
        // ignore
      }
      resolve({ ping: best, colo });
    };
    const guard = setTimeout(done, 6000);
    const pingOnce = (): void => {
      sent++;
      t0 = performance.now();
      ws.send('p');
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string' && ev.data.startsWith('{')) {
        colo = coloName((JSON.parse(ev.data) as { colo?: string }).colo);
        pingOnce();
        return;
      }
      best = Math.min(best, performance.now() - t0);
      if (sent < 3) pingOnce();
      else done();
    };
    ws.onerror = done;
    ws.onclose = () => {
      clearTimeout(guard);
      resolve({ ping: best, colo });
    };
  });
}

const COLO_NAMES: Record<string, string> = {
  HKG: 'Hong Kong', SIN: 'Singapore', NRT: 'Tokyo', HND: 'Tokyo', KIX: 'Osaka', ICN: 'Seoul', TPE: 'Taipei',
  BOM: 'Mumbai', SYD: 'Sydney', MEL: 'Melbourne', AKL: 'Auckland',
  LAX: 'Los Angeles', SJC: 'San Jose', SEA: 'Seattle', IAD: 'Virginia', EWR: 'New York', ORD: 'Chicago', DFW: 'Dallas', ATL: 'Atlanta', YYZ: 'Toronto',
  GRU: 'São Paulo', EZE: 'Buenos Aires', SCL: 'Santiago',
  FRA: 'Frankfurt', LHR: 'London', AMS: 'Amsterdam', CDG: 'Paris', MAD: 'Madrid', WAW: 'Warsaw', VIE: 'Vienna', PRG: 'Prague',
  DXB: 'Dubai', TLV: 'Tel Aviv', JNB: 'Johannesburg', CPT: 'Cape Town',
};

function coloName(colo: string | undefined): string | null {
  if (!colo) return null;
  return COLO_NAMES[colo] ?? colo;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, text: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.textContent = text;
  return e;
}
